import { confirmacaoConfere } from '@reydelgreen/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { ColorPicker } from '../../components/ColorPicker'
import { Modal } from '../../components/Modal'
import { numero } from '../../lib/format'
import { t, tRico } from '../../lib/i18n'
import { useWorkspaceRole } from '../../lib/role'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/tags')({ component: TagsPage })

/**
 * Tags do workspace.
 *
 * Saiu de dentro de Configurações: tag é ferramenta de quem atende todo dia,
 * não ajuste que se faz uma vez. Quem atende cria tag digitando no perfil do
 * lead, então também renomeia e troca a cor aqui — só apagar continua sendo de
 * administrador, porque tirar a marca de centenas de leads não tem desfazer.
 */
function TagsPage() {
  const { isAdmin } = useWorkspaceRole()
  const [criando, setCriando] = useState(false)

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-3xl px-5 pt-4 md:pt-8">
        {/* Título e botão na mesma linha, descrição embaixo: no celular a
            descrição é longa e derrubaria o botão para a linha seguinte. */}
        <div className="flex items-center gap-3">
          <h1 className="min-w-0 font-display text-2xl">{t('Tags')}</h1>
          <button
            type="button"
            onClick={() => setCriando(true)}
            className="ml-auto shrink-0 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:bg-accent-ink"
          >
            {t('Nova tag')}
          </button>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-ink-2">
          {t(
            'As tags são do workspace inteiro: renomear ou trocar a cor muda em todos os leads marcados.',
          )}
          {isAdmin && ` ${t('Apagar tira a marca de todos eles — o lead continua onde está.')}`}
        </p>

        <div className="mt-6">
          <TagsSection criando={criando} onFecharCriacao={() => setCriando(false)} />
        </div>
      </div>
    </div>
  )
}

/**
 * Cor sugerida para a próxima tag.
 *
 * A mesma paleta que o servidor usa quando ninguém escolhe, na mesma ordem:
 * assim o que a janela mostra antes de criar é exatamente o que sairia sem
 * escolha nenhuma — e quem quiser outra, troca ali mesmo.
 */
const CORES_PADRAO = ['#BC3A2B', '#0E6B5E', '#96570A', '#2258B8', '#8A3E7C']

/** Formulário de criação: nome e cor. A cor ainda pode ser trocada na lista. */
function NovaTag({ quantas, onCriada }: { quantas: number; onCriada: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [cor, setCor] = useState(() => CORES_PADRAO[quantas % CORES_PADRAO.length]!)

  const criar = useMutation({
    ...trpc.lead.createTag.mutationOptions(),
    onSuccess: () => {
      setLabel('')
      queryClient.invalidateQueries({ queryKey: [['lead']] })
      onCriada()
    },
  })

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        const nome = label.trim()
        if (nome) criar.mutate({ label: nome, color: cor })
      }}
      className="flex flex-wrap items-center gap-2 p-4"
    >
      {/* A bolinha vem antes do nome, como na lista: é o mesmo controle no
          mesmo lugar, só que antes de a tag existir. */}
      <ColorPicker
        color={cor}
        label={t('da nova tag')}
        className="size-4 flex-none"
        onChange={setCor}
      />

      <input
        // biome-ignore lint/a11y/noAutofocus: o formulário só aparece depois do clique em "Nova tag"
        autoFocus
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        maxLength={24}
        placeholder={t('Nome da tag')}
        aria-label={t('Nome da tag')}
        className="min-w-0 flex-1 rounded-md border border-line bg-surface px-2 py-1.5 text-sm"
      />
      <button
        type="submit"
        disabled={criar.isPending || !label.trim()}
        className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-2xs font-semibold text-on-accent disabled:opacity-50"
      >
        {criar.isPending ? 'Criando…' : 'Criar'}
      </button>
      {criar.error && <p className="basis-full text-2xs text-danger">{t(criar.error.message)}</p>}
    </form>
  )
}

function TagsSection({
  criando,
  onFecharCriacao,
}: {
  criando: boolean
  onFecharCriacao: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const tags = useQuery(trpc.lead.tagsAdmin.queryOptions())
  const { isAdmin } = useWorkspaceRole()

  const [editando, setEditando] = useState<string | null>(null)
  const [rascunho, setRascunho] = useState('')
  const [confirmando, setConfirmando] = useState<string | null>(null)
  const [apagandoContatos, setApagandoContatos] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  function recarregar() {
    queryClient.invalidateQueries({ queryKey: [['lead']] })
    queryClient.invalidateQueries({ queryKey: [['conversation']] })
  }

  const renomear = useMutation({
    ...trpc.lead.renameTag.mutationOptions(),
    onSuccess: () => {
      setEditando(null)
      setErro(null)
      recarregar()
    },
    onError: (error) => setErro(error.message),
  })

  const corDaTag = useMutation({
    ...trpc.lead.setTagColor.mutationOptions(),
    onSuccess: recarregar,
    onError: (error) => setErro(error.message),
  })

  const apagar = useMutation({
    ...trpc.lead.removeTagFromWorkspace.mutationOptions(),
    onSuccess: () => {
      setConfirmando(null)
      recarregar()
    },
    onError: (error) => setErro(error.message),
  })

  return (
    <div>
      {criando && (
        <Modal
          title={t('Nova tag')}
          description={t(
            'A cor já vem sugerida pela paleta; trocar aqui ou depois, na lista, dá no mesmo.',
          )}
          onClose={onFecharCriacao}
          largura="max-w-md"
        >
          <NovaTag quantas={(tags.data ?? []).length} onCriada={onFecharCriacao} />
        </Modal>
      )}

      {tags.isPending && <p className="text-xs text-ink-3">{t('Carregando…')}</p>}

      {!tags.isPending && (tags.data ?? []).length === 0 && (
        <p className="text-xs text-ink-3">
          Nenhuma tag ainda. Além do botão aí em cima, elas nascem no chat: digite o nome em “+ tag”
          no perfil do lead.
        </p>
      )}

      <ul className="flex flex-col gap-1.5">
        {(tags.data ?? []).map((tag) => (
          <li
            key={tag.id}
            className="flex flex-wrap items-center gap-2 rounded-lg border border-line px-3 py-2"
          >
            <ColorPicker
              color={tag.color}
              label={t('tag {nome}', { nome: tag.label })}
              className="size-3"
              onChange={(color) => corDaTag.mutate({ tagId: tag.id, color })}
            />

            {editando === tag.id ? (
              <form
                className="flex min-w-0 flex-1 items-center gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  const label = rascunho.trim()
                  if (label) renomear.mutate({ tagId: tag.id, label })
                }}
              >
                {/* `biome-ignore`: o foco automático aqui é o ponto — a pessoa
                    clicou em "Renomear" e o próximo gesto é digitar. */}
                <input
                  // biome-ignore lint/a11y/noAutofocus: foco pedido pelo clique anterior
                  autoFocus
                  // Texto todo selecionado: renomear quase sempre é trocar o
                  // nome inteiro, não emendar no que já estava lá.
                  onFocus={(event) => event.target.select()}
                  value={rascunho}
                  onChange={(event) => setRascunho(event.target.value)}
                  maxLength={24}
                  className="min-w-0 flex-1 rounded-md border border-line bg-surface-2 px-2 py-1 text-sm focus-visible:bg-surface"
                />
                <button
                  type="submit"
                  disabled={renomear.isPending}
                  className="rounded-md bg-accent px-3 py-1.5 text-2xs font-semibold text-on-accent disabled:opacity-50"
                >
                  {t('Salvar')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditando(null)
                    setErro(null)
                  }}
                  className="text-2xs font-semibold text-ink-3 hover:text-ink"
                >
                  {t('Cancelar')}
                </button>
              </form>
            ) : (
              <>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">{tag.label}</span>
                <span className="flex-none text-2xs text-ink-3">
                  {tag.total === 1
                    ? t('{n} lead', { n: tag.total })
                    : t('{n} leads', { n: tag.total })}
                </span>

                {/* Botões de verdade, com moldura: como texto solto liam como
                    legenda e ninguém achava que dava para clicar. */}
                <button
                  type="button"
                  onClick={() => {
                    setEditando(tag.id)
                    setRascunho(tag.label)
                    setConfirmando(null)
                    setErro(null)
                  }}
                  className="flex-none rounded-md border border-line bg-surface-2 px-2.5 py-1 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:bg-surface-3 hover:text-ink"
                >
                  {t('Renomear')}
                </button>

                {/* Apagar continua sendo `adminProcedure` no servidor — sem a
                    checagem aqui o atendente veria um botão que sempre falha.

                    Dois passos em vez de `confirm()`: o diálogo do navegador
                    trava a aba inteira e não dá para dizer o que se perde. */}
                {!isAdmin ? null : confirmando === tag.id ? (
                  <span className="flex flex-none items-center gap-2">
                    <button
                      type="button"
                      onClick={() => apagar.mutate({ tagId: tag.id })}
                      disabled={apagar.isPending}
                      className="rounded-md bg-danger px-2.5 py-1 text-2xs font-semibold text-white disabled:opacity-50"
                    >
                      {apagar.isPending ? t('Apagando…') : t('Apagar de {n}', { n: tag.total })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmando(null)}
                      className="rounded-md border border-line bg-surface-2 px-2.5 py-1 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:bg-surface-3 hover:text-ink"
                    >
                      {t('Cancelar')}
                    </button>
                  </span>
                ) : (
                  <>
                    {/* Apagar os contatos é outra coisa: "Apagar" tira só a
                        marca. Ficam lado a lado porque a pergunta que traz
                        alguém aqui depois de uma importação errada é "como
                        removo essas pessoas", e a tag é o nome do conjunto. */}
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmando(tag.id)
                        setEditando(null)
                        setErro(null)
                      }}
                      className="flex-none rounded-md border border-danger/30 bg-danger-soft px-2.5 py-1 text-2xs font-semibold text-danger hover:border-danger/60"
                    >
                      {t('Apagar')}
                    </button>

                    {tag.total > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setApagandoContatos(tag.id)
                          setEditando(null)
                          setConfirmando(null)
                          setErro(null)
                        }}
                        className="flex-none rounded-md border border-danger/30 bg-danger-soft px-2.5 py-1 text-2xs font-semibold text-danger hover:border-danger/60"
                      >
                        {t('Apagar contatos')}
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      {apagandoContatos && (
        <ApagarContatosDaTag
          tagId={apagandoContatos}
          tag={(tags.data ?? []).find((linha) => linha.id === apagandoContatos)}
          onClose={() => setApagandoContatos(null)}
        />
      )}

      {erro && <p className="mt-2 text-2xs text-danger">{erro}</p>}
    </div>
  )
}

/**
 * Apaga os contatos de uma tag, em lotes no servidor.
 *
 * É o desfazer de uma importação: o arquivo entrou marcado, e a marca é o que
 * nomeia o conjunto. Sem isto, corrigir uma importação de 30 mil exigiria
 * apagar 30 mil contatos um a um.
 *
 * Confirmação por nome digitado, como nas outras exclusões grandes: o risco
 * aqui não é clicar sem querer, é apagar a tag certa por engano.
 */
function ApagarContatosDaTag({
  tagId,
  tag,
  onClose,
}: {
  tagId: string
  tag?: { label: string; total: number }
  onClose: () => void
}) {
  const trpc = useTRPC()
  const [digitado, setDigitado] = useState('')
  const [jobId, setJobId] = useState<string | null>(null)

  const disparar = useMutation({
    ...trpc.bulk.acaoPorTag.mutationOptions(),
    onSuccess: (resultado) => setJobId(resultado.jobId),
  })

  const jobs = useQuery({
    ...trpc.bulk.list.queryOptions(),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const atual = query.state.data?.find((linha) => linha.id === jobId)
      return atual && (atual.status === 'pendente' || atual.status === 'processando') ? 1000 : false
    },
  })

  const atual = jobs.data?.find((linha) => linha.id === jobId) ?? null
  const nome = tag?.label ?? ''
  const confere = confirmacaoConfere(digitado, nome)

  return (
    <Modal
      onClose={onClose}
      title={t('Apagar os contatos de “{nome}”', { nome })}
      description={t(
        'Os contatos somem do CRM com todas as conversas e mensagens deles. Não há como desfazer.',
      )}
      largura="max-w-md"
    >
      <div className="p-4">
        {jobId ? (
          <>
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-ink">
                {atual?.status === 'concluido' ? t('Concluído') : t('Apagando')}
              </h3>
              <span className="text-xs tabular-nums text-ink-2">
                {t('{n} apagados', { n: numero(atual?.processed ?? 0) })}
              </span>
            </div>
            {/* Sem barra: não há total conhecido de antemão — o trabalho
                consome do filtro enquanto sobrar, e inventar uma porcentagem
                seria inventar o denominador. */}
            <p className="mt-1.5 text-2xs text-ink-3">
              {atual?.status === 'concluido'
                ? t('Pode fechar.')
                : t('Pode fechar esta janela: o trabalho continua no servidor.')}
            </p>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-on-accent"
              >
                {t('Fechar')}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm leading-relaxed text-ink-2">
              {tRico(
                tag?.total === 1
                  ? 'Isto apaga {n} contato e todo o histórico de conversa dele.'
                  : 'Isto apaga {n} contatos e todo o histórico de conversa deles.',
                { n: <strong className="text-danger">{tag?.total ?? 0}</strong> },
              )}
            </p>

            <label className="mt-4 block">
              <span className="mb-1.5 block text-2xs font-semibold text-ink-2">
                {tRico('Digite {nome} para confirmar', {
                  nome: <strong className="text-ink">{nome}</strong>,
                })}
              </span>
              <input
                value={digitado}
                onChange={(evento) => setDigitado(evento.target.value)}
                autoComplete="off"
                className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
              />
            </label>

            {digitado.trim().length > 0 && !confere && (
              <p className="mt-1.5 text-2xs text-ink-3">
                {tRico('Ainda não confere com {nome}.', {
                  nome: <strong className="text-ink-2">{nome}</strong>,
                })}
              </p>
            )}

            {disparar.error && (
              <p role="alert" className="mt-2 text-2xs text-danger">
                {t(disparar.error.message)}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-3 py-2 text-sm font-semibold text-ink-2 hover:bg-surface-2"
              >
                {t('Cancelar')}
              </button>
              <button
                type="button"
                disabled={!confere || disparar.isPending}
                onClick={() => disparar.mutate({ tagId, kind: 'delete_contacts' })}
                className="rounded-md bg-danger px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
              >
                {t('Apagar contatos')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
